import type { LucideIcon } from 'lucide-react';

/** The tones the inquiry row uses, kept here so no call site hand-writes classes. */
const TONES = {
  primary: 'bg-primary-50 text-primary-700 hover:bg-primary-100',
  success: 'bg-green-50 text-green-700 hover:bg-green-100',
  warning: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100',
  danger: 'bg-red-50 text-red-700 hover:bg-red-100',
  neutral: 'bg-gray-50 text-gray-600 hover:bg-gray-100',
} as const;

export type InquiryActionTone = keyof typeof TONES;

interface InquiryActionButtonProps {
  icon: LucideIcon;
  label: string;
  /** Hover text, and the accessible handle the tests select rows by. */
  title: string;
  tone: InquiryActionTone;
  onClick: () => void;
  disabled?: boolean;
}

/**
 * One action on an inquiry row.
 *
 * ⚠️ **Five buttons differing only in icon, label and colour is the duplication
 * this repository tells you not to write.** Convert, Contacted, Spam, Not spam
 * and Archive were five copies of the same twelve lines, so a change to the
 * shape of an action meant five edits and four chances to miss one. `tone`
 * names the intent and this file owns the classes.
 */
export function InquiryActionButton({
  icon: Icon,
  label,
  title,
  tone,
  onClick,
  disabled,
}: InquiryActionButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${TONES[tone]}`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
